// Tooltip primitive (Unit 36b, spec §11.10).
//
// Thin wrapper around Radix Tooltip. Mount `<TooltipProvider>` once at the
// app root so `delayDuration` and `skipDelayDuration` apply group-wide. The
// spec inventory in `tooltips/inventory.ts` is the source of truth for the
// copy strings — components that need a tooltip should pass a key from that
// inventory, not a raw string.

import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useIsTouchDevice } from "../device/useDeviceInfo";
import { TOOLTIP_KEYS, type TooltipKey } from "../tooltips/inventory";

import "./Tooltip.css";

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RadixTooltip.Provider delayDuration={400} skipDelayDuration={0}>
      {children}
    </RadixTooltip.Provider>
  );
}

interface TooltipProps {
  /** Inventory key. Resolves to a translated string via `tooltip.{key}`. */
  tipKey: TooltipKey;
  /** Override text if the control's tooltip is dynamic and not in the
   * inventory. Prefer adding to the inventory instead. */
  text?: string;
  /** The trigger element. Will receive `aria-label` automatically. */
  children: ReactNode;
  /** Disable on touch devices — long-press is reserved for the canvas. */
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}

export function Tooltip({ tipKey, text, children, side = "bottom", align = "center" }: TooltipProps) {
  const { t } = useTranslation();
  const isTouch = useIsTouchDevice();
  const resolved = text ?? t(`tooltip.${tipKey}`);

  if (isTouch) {
    return <>{children}</>;
  }

  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          className="ophelia-tooltip"
        >
          {resolved}
          <RadixTooltip.Arrow className="ophelia-tooltip__arrow" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

// Re-export the keys list for tests / inventory consumers.
export { TOOLTIP_KEYS };
